import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState } from "react";
/** Matches `.sl-cart-bump`'s keyframe duration in styles.css — kept as one named
 * constant so the JS class-removal timer and the CSS animation length can't quietly
 * drift apart. */
const BUMP_ANIMATION_MS = 300;
/**
 * Cart icon (inline SVG — no icon library exists in this repo) + a numeric badge that
 * only renders when `count > 0`. Bumps via the `sl-cart-bump` keyframe (styles.css)
 * whenever `count` changes, removing the animation class after `BUMP_ANIMATION_MS` so it
 * can re-trigger on the next change. (Deliberately a timer rather than `onAnimationEnd`:
 * jsdom does not reliably dispatch `animationend` through React's synthetic event system,
 * which would make this untestable — a plain timer keyed to the same duration as the CSS
 * keyframe is equally correct in a real browser and is testable with fake timers.)
 *
 * Forwards its ref to the underlying `<button>` so a consumer can pass this element's
 * DOM node as `flyToCart`'s `targetEl`.
 */
export const CartBadge = forwardRef(function CartBadge({ count, onClick, ariaLabel = "Cart" }, ref) {
    const previousCount = useRef(count);
    const [bump, setBump] = useState(false);
    useEffect(() => {
        if (previousCount.current === count)
            return;
        previousCount.current = count;
        setBump(true);
        const timeout = window.setTimeout(() => setBump(false), BUMP_ANIMATION_MS);
        return () => window.clearTimeout(timeout);
    }, [count]);
    const label = count > 0 ? `${ariaLabel} (${count} item${count === 1 ? "" : "s"})` : ariaLabel;
    return (_jsxs("button", { type: "button", ref: ref, className: ["sl-cart-badge", bump ? "sl-cart-badge--bump" : null].filter(Boolean).join(" "), onClick: onClick, "aria-label": label, children: [_jsxs("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "none", stroke: "currentColor", strokeWidth: "2", "aria-hidden": "true", children: [_jsx("path", { d: "M6 8h12l-1.2 10.2a2 2 0 0 1-2 1.8H9.2a2 2 0 0 1-2-1.8L6 8Z", strokeLinejoin: "round" }), _jsx("path", { d: "M9 8V6a3 3 0 0 1 6 0v2", strokeLinecap: "round" })] }), count > 0 ? (_jsx("span", { className: "sl-cart-badge__count", "data-testid": "cart-badge-count", children: count > 99 ? "99+" : count })) : null] }));
});
//# sourceMappingURL=CartBadge.js.map